import { ExpandablePanel } from "@/components/dashboard/ExpandablePanel";
import { TriLanguageBlock, MantraTriLanguageBlock } from "@/components/i18n/TriLanguageBlock";
import {
  hasSanskritContent,
  normalizeTriLanguageItem,
  storyToTriItem,
  sadhanaStepToTriItem,
  toTriFromFields,
} from "@/lib/i18n/tri-language";
import { renderMantraText } from "@/lib/audio/renderMantraText";
import { SadhanaStep } from "@/components/audio/AiVoiceCards";

function audioModeForLabel(label) {
  const l = String(label || "").toLowerCase();
  if (l.includes("story")) return "story";
  if (l.includes("sādhana") || l.includes("sadhana") || l.includes("instruction")) return "sadhana";
  if (l.includes("commentary") || l.includes("explanation")) return "commentary";
  return "meaning";
}

function ListBlock({ title, items, token, mode = "meaning" }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="text-xs font-medium text-foreground mb-1">{title}</p>
      <ul className="list-disc pl-4 space-y-0.5">
        {items.map((x, i) => (
          <li key={i} className={hasSanskritContent(String(x)) ? "mantra-line" : ""}>
            {hasSanskritContent(String(x)) ? (
              renderMantraText(String(x), { textClassName: "flex-1" })
            ) : mode === "sadhana" ? (
              <SadhanaStep text={String(x)} index={i} token={token} className="list-none -ml-4" />
            ) : (
              <TriLanguageBlock
                item={toTriFromFields({ english: String(x), summary_en: String(x) })}
                meaningPrefix="summary"
                compact
                token={token}
                showMeaningAudio
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BeginnerDeityView({ entry, token }) {
  const b = entry.beginner;
  return (
    <div className="space-y-4" data-testid="sacred-beginner">
      <p className="text-lg">{b.identity}</p>
      <Row label="Meaning" value={b.meaning} token={token} />
      <Row label="Why it matters" value={b.why_it_matters} token={token} />
      <Row label="Key mantra" value={b.key_mantra} token={token} />
      <Row label="Story" value={b.short_story} token={token} mode="story" />
      <Row label="Simple sādhana" value={b.simple_sadhana} token={token} mode="sadhana" />
      <DeepDeityPanels entry={entry} token={token} />
    </div>
  );
}

function DeepDeityPanels({ entry, token }) {
  const d = entry.deep;
  return (
    <div className="space-y-2 pt-4">
      <ExpandablePanel title="Tattva & essence" summary={d.tattva?.slice(0, 60)} testId="deep-tattva">
        <TriLanguageBlock
          item={toTriFromFields({ english: d.tattva, translation_en: d.tattva })}
          meaningPrefix="translation"
          token={token}
        />
        <ListBlock title="Iconography" items={d.iconography} token={token} />
      </ExpandablePanel>
      <ExpandablePanel title="Stories" summary={`${d.stories?.length ?? 0} narratives`} testId="deep-stories">
        <ListBlock items={d.stories} token={token} mode="story" />
      </ExpandablePanel>
      <ExpandablePanel title="Mantras & stotras" summary="Chant and praise" testId="deep-mantras">
        <ListBlock title="Mantras" items={d.mantras} token={token} />
        <ListBlock title="Stotras" items={d.stotras} token={token} />
      </ExpandablePanel>
      <ExpandablePanel title="Pūjā & sādhana" summary="Ritual depth" testId="deep-puja">
        <ListBlock title="Pūjā vidhi" items={d.puja_vidhi} token={token} mode="sadhana" />
        <ListBlock title="Sādhana guide" items={d.sadhana_guide} token={token} mode="sadhana" />
      </ExpandablePanel>
      <ExpandablePanel title="Guidelines" summary="Do, don't, charity" testId="deep-guidelines">
        <ListBlock title="Do & don't" items={d.do_and_dont} />
        <ListBlock title="Charity" items={d.charity} />
        <ListBlock title="Temples" items={d.temples} />
        <ListBlock title="Scriptural refs" items={d.scriptural_refs} />
        <ListBlock title="Astrological notes" items={d.astrological_notes} />
      </ExpandablePanel>
    </div>
  );
}

export function BeginnerFestivalView({ entry }) {
  const b = entry.beginner;
  const d = entry.deep;
  return (
    <div className="space-y-4" data-testid="sacred-beginner">
      <Row label="Meaning" value={b.meaning} />
      <Row label="Why celebrated" value={b.why_celebrated} />
      <Row label="Key mantra" value={b.key_mantra} />
      <Row label="Simple pūjā" value={b.simple_puja} />
      <DeepPanels
        sections={[
          { title: "Origin story", summary: d.origin_story?.slice(0, 50), body: <p>{d.origin_story}</p> },
          { title: "Pūjā vidhi", summary: "Detailed steps", body: <ListBlock items={d.puja_vidhi} /> },
          { title: "Sādhana & mantras", summary: "Practice", body: (<><ListBlock title="Sādhana" items={d.sadhana} /><ListBlock title="Mantras" items={d.mantras} /></>) },
          { title: "Rules & charity", summary: "Food and conduct", body: (<><ListBlock title="Do & don't" items={d.do_and_dont} /><ListBlock title="Food" items={d.food_rules} /><ListBlock title="Charity" items={d.charity} /><ListBlock title="Scriptural refs" items={d.scriptural_refs} /><ListBlock title="Astrology" items={d.astrological_notes} /></>) },
        ]}
      />
    </div>
  );
}

export function BeginnerVrataView({ entry }) {
  const b = entry.beginner;
  const d = entry.deep;
  return (
    <div className="space-y-4" data-testid="sacred-beginner">
      <Row label="Meaning" value={b.meaning} />
      <ListBlock title="Simple rules" items={b.simple_rules} />
      <Row label="Key mantra" value={b.key_mantra} />
      <DeepPanels
        sections={[
          { title: "Timings", summary: "When to observe", body: <ListBlock items={d.timings} /> },
          { title: "Food guidelines", summary: "Diet", body: <ListBlock items={d.food_guidelines} /> },
          { title: "Full rules & benefits", summary: "Complete vrata", body: (<><ListBlock title="Rules" items={d.full_rules} /><ListBlock title="Benefits" items={d.benefits} /><ListBlock title="Sādhana" items={d.sadhana} /><ListBlock title="Mantras" items={d.mantras} /><ListBlock title="Scriptural refs" items={d.scriptural_refs} /></>) },
        ]}
      />
    </div>
  );
}

export function BeginnerStoryView({ entry, token }) {
  const b = entry.beginner;
  const d = entry.deep;
  const storyItem = storyToTriItem({
    canonical: {
      summary_en: b.summary,
      full_story: d.full_story,
      moral: b.moral,
    },
  });

  return (
    <div className="space-y-4" data-testid="sacred-beginner">
      <TriLanguageBlock item={storyItem} meaningPrefix="summary" meaningAudioMode="story" token={token} />
      {b.moral && (
        <TriLanguageBlock
          item={toTriFromFields({ english: b.moral, translation_en: b.moral })}
          meaningPrefix="translation"
          englishLabel="Moral"
          token={token}
          compact
        />
      )}
      <DeepPanels
        token={token}
        sections={[
          {
            title: "Full story",
            summary: "Expanded narrative",
            body: (
              <TriLanguageBlock
                item={storyToTriItem({ canonical: { summary_en: d.full_story, full_story: d.full_story } })}
                meaningPrefix="summary"
                meaningAudioMode="story"
                token={token}
              />
            ),
          },
          {
            title: "Sources & insight",
            summary: "Scripture",
            body: (
              <>
                <ListBlock title="Characters" items={d.characters} token={token} />
                <Row label="Source" value={d.scriptural_source} token={token} />
                <Row label="Sādhana insight" value={d.sadhana_insight} token={token} mode="sadhana" />
              </>
            ),
          },
        ]}
      />
    </div>
  );
}

export function BeginnerSadhanaView({ entry, token }) {
  const b = entry.beginner;
  const d = entry.deep;
  return (
    <div className="space-y-4" data-testid="sacred-beginner">
      <Row label="Purpose" value={b.purpose} token={token} mode="sadhana" />
      <ListBlock title="Steps" items={b.steps} token={token} mode="sadhana" />
      <Row label="Mantra" value={b.mantra} token={token} />
      <DeepPanels
        token={token}
        sections={[
          {
            title: "Breathing & mudrās",
            summary: "Technique",
            body: (
              <>
                <ListBlock title="Breathing" items={d.breathing} token={token} mode="sadhana" />
                <ListBlock title="Mudrās" items={d.mudras} token={token} mode="sadhana" />
              </>
            ),
          },
          {
            title: "Duration & precautions",
            summary: "Safety",
            body: (
              <>
                <ListBlock title="Duration" items={d.duration} token={token} mode="sadhana" />
                <ListBlock title="Precautions" items={d.precautions} token={token} mode="sadhana" />
                <ListBlock title="Scriptural refs" items={d.scriptural_refs} token={token} />
              </>
            ),
          },
        ]}
      />
    </div>
  );
}

export function BeginnerMantraView({ entry, token }) {
  return (
    <div className="space-y-4" data-testid="sacred-beginner">
      <MantraTriLanguageBlock
        item={{
          sanskrit_devanagari: entry.text,
          translation_en: entry.meaning || entry.beginner_summary,
          ...entry,
        }}
        meaningPrefix="translation"
        textClassName="text-2xl"
        token={token}
      />
      <Row label="Summary" value={entry.beginner_summary} token={token} />
      <ExpandablePanel title="Meaning & usage" summary="Deep layer" testId="deep-mantra">
        <Row label="Meaning" value={entry.meaning} token={token} />
        <Row label="Usage" value={entry.usage} token={token} mode="sadhana" />
      </ExpandablePanel>
    </div>
  );
}

function DeepPanels({ sections, token }) {
  return (
    <div className="space-y-2 pt-4">
      {sections.map((s, i) => (
        <ExpandablePanel key={i} title={s.title} summary={s.summary} testId={`deep-${i}`}>
          {s.body}
        </ExpandablePanel>
      ))}
    </div>
  );
}

function Row({ label, value, className = "", token, mode }) {
  if (!value) return null;
  const item = normalizeTriLanguageItem(
    typeof value === "string" ? { text: value } : value
  );
  const audioMode = mode || audioModeForLabel(label);
  return (
    <div>
      {hasSanskritContent(item) ? (
        <>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
          <div className={`text-sm ${className}`}>
            <MantraTriLanguageBlock item={item} meaningPrefix="translation" compact token={token} />
          </div>
        </>
      ) : (
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
          <TriLanguageBlock
            item={
              audioMode === "story"
                ? toTriFromFields({ english: typeof value === "string" ? value : String(value), summary_en: typeof value === "string" ? value : String(value) })
                : toTriFromFields({ english: typeof value === "string" ? value : String(value), translation_en: typeof value === "string" ? value : String(value) })
            }
            meaningPrefix={audioMode === "story" ? "summary" : "translation"}
            meaningAudioMode={audioMode}
            compact
            token={token}
            className={className}
          />
        </div>
      )}
    </div>
  );
}
